"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useUnavailability = exports.UnavailabilityProvider = exports.UnavailabilityContext = void 0;
const const_1 = require("@app/const");
const util_1 = require("@app/util");
const use_workspace_unavailability_categories_1 = require("@app/util/hooks/use-workspace-unavailability-categories");
const moment_1 = __importDefault(require("moment"));
const react_1 = __importDefault(require("react"));
const react_hook_form_1 = require("react-hook-form");
const use_default_details_middleware_1 = require("./util/middleware/use-default-details-middleware");
const use_modal_section_toggled_middleware_1 = require("./util/middleware/use-modal-section-toggled-middleware");
const use_request_absence_middleware_1 = require("./util/middleware/use-request-absence-middleware");
const use_save_unavailability_middleware_1 = require("./util/middleware/use-save-unavailability-middleware");
const use_sync_sections_middleware_1 = require("./util/middleware/use-sync-sections-middleware");
exports.UnavailabilityContext = react_1.default.createContext(undefined);
const UnavailabilityProvider = ({ children, initialData, isEmployeeCalendar = false, afterSubmit }) => {
    const _isEmployeeCalendar = typeof isEmployeeCalendar === 'string' || isEmployeeCalendar;
    const [selectedTab, setSelectedTab] = react_1.default.useState('overview');
    const [toggledSections, setToggledSections] = react_1.default.useState(['whole-day', 'default-work-minutes-inclusion']);
    const { canWrite } = (0, util_1.useAccessRole)();
    // const me = useRootSelector((state) => state.me)
    // const isEmployeeCalendar = isValue(me?.id) && !canWrite(PERMISSIONS.MNG_CALENDAR)
    const isEditing = react_1.default.useRef((0, util_1.isValue)(initialData?.id));
    const canEdit = react_1.default.useRef(canWrite(const_1.PERMISSIONS.MNG_CALENDAR));
    const [loading, setLoading] = react_1.default.useState(false);
    const [initialLoad, setInitialLoading] = react_1.default.useState(true);
    const [timePeriodInput, setTimePeriodInput] = react_1.default.useState({
        // we actually dont care about date, only about time
        // so we can use todays date (call moment without arg)
        start: (0, moment_1.default)().startOf('day'),
        end: (0, moment_1.default)().endOf('day'),
    });
    const { add, remove, triggerSubscriptions } = (0, use_modal_section_toggled_middleware_1.useModalSectionToggledMiddleware)();
    const toggleSection = react_1.default.useCallback((section, visible) => {
        setToggledSections((prev) => {
            const _new = Array.from(prev);
            triggerSubscriptions(section, visible ? 'on' : 'off');
            if (visible) {
                if (section === 'whole-day') {
                    setTimeout(() => toggleSection('period', false), 0);
                }
                if (section === 'default-work-minutes-inclusion') {
                    setTimeout(() => toggleSection('work-minutes-inclusion', false), 0);
                }
                return [..._new, section];
            }
            if (section === 'whole-day') {
                setTimeout(() => toggleSection('period', true), 0);
            }
            if (section === 'default-work-minutes-inclusion') {
                setTimeout(() => toggleSection('work-minutes-inclusion', true), 0);
            }
            return _new.filter((sec) => sec !== section);
        });
    }, [setToggledSections, triggerSubscriptions]);
    const getDefaultDetails = (0, use_default_details_middleware_1.useDefaultDetailsMiddleware)(initialData, setTimePeriodInput);
    const initialDetailsPromise = react_1.default.useMemo(async () => await getDefaultDetails(), 
    // should be called only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    // eslint-disable-next-line @typescript-eslint/promise-function-async
    const defaultDetails = react_1.default.useRef(initialDetailsPromise);
    const syncSections = (0, use_sync_sections_middleware_1.useSyncSectionsMiddleware)({ toggleSection });
    react_1.default.useEffect(() => {
        defaultDetails.current
            .then((res) => {
            syncSections(res);
        })
            .finally(() => setTimeout(() => setInitialLoading(false), 100));
    }, [syncSections, defaultDetails, setInitialLoading]);
    const { control, getValues, handleSubmit, setValue } = (0, react_hook_form_1.useForm)({
        defaultValues: async () => await defaultDetails.current,
        mode: 'all',
        reValidateMode: 'onChange',
    });
    const categoryId = getValues().categoryId;
    const isRequestingAbsence = _isEmployeeCalendar &&
        categoryId !== use_workspace_unavailability_categories_1.AVAILABILITY_VALUE &&
        categoryId !== use_workspace_unavailability_categories_1.UNAVAILABILITY_VALUE &&
        !isEditing.current;
    const saveMiddleware = (0, use_save_unavailability_middleware_1.useSaveUnavailabilityMiddleware)(_isEmployeeCalendar);
    const requestAbsenceMiddleware = (0, use_request_absence_middleware_1.useRequestAbsenceMiddleware)();
    const _handleSubmit = handleSubmit(isRequestingAbsence ? requestAbsenceMiddleware : saveMiddleware);
    const context = {
        selectedTab,
        setSelectedTab,
        formControl: control,
        getValues,
        setValue,
        handleSubmit: _handleSubmit,
        toggledSections,
        toggleSection,
        addSectionListener: add,
        removeSectionListener: remove,
        isEditing: isEditing.current,
        canEdit: canEdit.current,
        isRequestingAbsence,
        loading,
        setLoading,
        initialLoad,
        isEmployeeCalendar: _isEmployeeCalendar,
        initialData,
        afterSubmit,
        timePeriodInput,
        setTimePeriodInput,
    };
    return (react_1.default.createElement(exports.UnavailabilityContext.Provider, { value: context }, children?.(context)));
};
exports.UnavailabilityProvider = UnavailabilityProvider;
const useUnavailability = () => {
    const unavailabilityContext = react_1.default.useContext(exports.UnavailabilityContext);
    if ((0, util_1.isUndefined)(unavailabilityContext)) {
        throw new Error('useUnavailability must be used within <UnavailabilityProvider>');
    }
    return unavailabilityContext;
};
exports.useUnavailability = useUnavailability;
